
import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

export function Contact() {
  const { t } = useTranslation('common');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('mensagem') as string;
    if (message && message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d4jh682eh8g2mrj4b1d0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contato" className="py-20 bg-white">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {t('contact.title')}
            </h2>
            <p className="text-xl text-gray-600 mb-12 leading-relaxed">
              {t('contact.subtitle')}
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{t('contact.phone')}</h3>
                  <a href="tel:+5521993197442" className="text-gray-600 hover:text-orange-500 transition-colors cursor-pointer text-lg">
                    (21) 99319-7442
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-blue-900 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-fill text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{t('contact.email')}</h3>
                  <a href="mailto:comercial@aplconsulttec.com.br" className="text-gray-600 hover:text-blue-900 transition-colors cursor-pointer text-lg break-all">
                    comercial@aplconsulttec.com.br
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-fill text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{t('contact.hours')}</h3>
                  <p className="text-gray-600 text-lg">{t('contact.hoursValue')}</p>
                </div>
              </div>
            </div>

            <div className="mt-12 p-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl text-white">
              <h3 className="text-2xl font-bold mb-4">{t('contact.whyChoose')}</h3>
              <ul className="space-y-3">
                {(t('contact.reasons', { returnObjects: true }) as string[]).map((reason, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-2xl flex-shrink-0"></i>
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-10 border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              {t('contact.formTitle')}
            </h3>
            
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="nome" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.formName')} *
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm"
                  placeholder={t('contact.formNamePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.formEmail')} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm"
                  placeholder={t('contact.formEmailPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="telefone" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.formPhone')} *
                </label>
                <input
                  type="tel"
                  id="telefone"
                  name="telefone"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm"
                  placeholder={t('contact.formPhonePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="empresa" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.formCompany')}
                </label>
                <input
                  type="text"
                  id="empresa"
                  name="empresa"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all text-sm"
                  placeholder={t('contact.formCompanyPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="servico" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.formService')} *
                </label>
                <div className="relative">
                  <select
                    id="servico"
                    name="servico"
                    required
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all appearance-none cursor-pointer text-sm bg-white"
                  >
                    <option value="">{t('contact.formServicePlaceholder')}</option>
                    <option value="Certificação INMETRO">{t('services.inmetro.title')}</option>
                    <option value="Homologação ANATEL">{t('services.anatel.title')}</option>
                    <option value="Registro ANVISA">{t('services.anvisa.title')}</option>
                    <option value="Registro de Fabricantes">{t('services.suppliers.title')}</option>
                    <option value="Acompanhamento de Testes">{t('services.testing.title')}</option>
                    <option value="Consultoria Especializada">{t('services.consulting.title')}</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none text-xl"></i>
                </div>
              </div>

              <div>
                <label htmlFor="mensagem" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('contact.formMessage')} *
                </label>
                <textarea
                  id="mensagem"
                  name="mensagem"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none text-sm"
                  placeholder={t('contact.formMessagePlaceholder')}
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{t('contact.formMaxChars')}</p>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <i className="ri-check-line text-2xl text-green-600"></i>
                  <p className="text-green-700 font-semibold">{t('contact.formSuccess')}</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                  <i className="ri-error-warning-line text-2xl text-red-600"></i>
                  <p className="text-red-700 font-semibold">{t('contact.formError')}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-lg text-lg"
              >
                {isSubmitting ? t('contact.formSubmitting') : t('contact.formSubmit')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
